import { Controller } from "@hotwired/stimulus"

/**
 * 4-step wizard for uploading GO4SPILOT documents.
 *
 * Steps:
 *   1. Document type  (card selection → category)
 *   2. File upload    (drag-and-drop / browse)
 *   3. Metadata       (title, description, year, cycle, service)
 *   4. Sharing        (confidentiality card + school select)
 */
export default class extends Controller {
  static targets = [
    "step",              // each step panel [data-step="1..4"]
    "sidebar",           // sidebar step items
    "progress",          // progress bar fill
    "progressLabel",     // "1 / 4 — 25 %"
    "footer",            // footer breadcrumb text
    "prevBtn",           // "Precedent" button
    "submitBtn",         // "Publier" (step 4 only)
    "nextBtn",           // "Continuer" button
    "categoryInput",     // hidden category input
    "confidentialityInput", // hidden confidentiality input
    "fileInput",         // hidden file input
    "filePreview",       // file preview container
    "fileName",          // file name text
    "fileSize",          // file size text
    "dropZone",          // drag-and-drop zone
    "titleInput",        // title text field (step 3)
    "rolesPanel",        // roles selector (step 4, internal)
    "usersPanel",        // users selector (step 4, confidential)
  ]

  static values = {
    current: { type: Number, default: 1 },
    total:   { type: Number, default: 4 },
  }

  connect() {
    this.selections = {
      category: null,
      confidentiality: null,
      hasFile: false,
    }

    this._syncInitialSelections()
    this._render()
  }

  _syncInitialSelections() {
    // Category (pre-selected from re-render on error)
    const selectedCat = this.element.querySelector(
      '[data-card-group="category"] .g4sp-wf-card--selected'
    )
    if (selectedCat) {
      this.selections.category = selectedCat.dataset.value
    }

    // Confidentiality
    const selectedConf = this.element.querySelector(
      '[data-card-group="confidentiality"] .g4sp-wf-card--selected'
    )
    if (selectedConf) {
      this.selections.confidentiality = selectedConf.dataset.value
      this._toggleConfidentialityPanels(selectedConf.dataset.value)
    }
  }

  // ──── Navigation ────
  next() {
    if (!this._validateStep(this.currentValue)) return
    if (this.currentValue < this.totalValue) {
      this.currentValue++
      this._render()
    }
  }

  prev() {
    if (this.currentValue > 1) {
      this.currentValue--
      this._render()
    }
  }

  goToStep(event) {
    const target = parseInt(event.currentTarget.dataset.step, 10)
    if (target < this.currentValue) {
      this.currentValue = target
      this._render()
    }
  }

  // ──── Card Selection ────
  selectCard(event) {
    const card = event.currentTarget
    const group = card.dataset.group
    const value = card.dataset.value

    // Deselect siblings
    const container = card.closest("[data-card-group]")
    if (container) {
      container.querySelectorAll("[data-group]").forEach((c) => {
        c.classList.remove("g4sp-wf-card--selected")
        const check = c.querySelector(".g4sp-wf-card-check")
        if (check) check.style.display = "none"
      })
    }

    card.classList.add("g4sp-wf-card--selected")
    const check = card.querySelector(".g4sp-wf-card-check")
    if (check) check.style.display = ""

    if (group === "category") {
      this.selections.category = value
      if (this.hasCategoryInputTarget) {
        this.categoryInputTarget.value = value
      }
    } else if (group === "confidentiality") {
      this.selections.confidentiality = value
      if (this.hasConfidentialityInputTarget) {
        this.confidentialityInputTarget.value = value
      }
      this._toggleConfidentialityPanels(value)
    }
  }

  // ──── Confidentiality Panels ────
  _toggleConfidentialityPanels(value) {
    if (this.hasRolesPanelTarget) {
      this.rolesPanelTarget.style.display =
        value === "internal" ? "" : "none"
    }
    if (this.hasUsersPanelTarget) {
      this.usersPanelTarget.style.display =
        value === "confidential" ? "" : "none"
    }
  }

  // ──── File Handling ────
  triggerFileInput(event) {
    // Don't trigger if clicking the remove button
    if (event.target.closest(".g4sp-wf-file-preview-remove")) return
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  handleFileSelect(event) {
    const file = event.currentTarget.files[0]
    if (file) this._showFilePreview(file)
  }

  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("g4sp-wf-dropzone--active")
    }
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("g4sp-wf-dropzone--active")
    }
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("g4sp-wf-dropzone--active")
    }

    const file = event.dataTransfer?.files?.[0]
    if (!file) return

    // Transfer to file input via DataTransfer
    const dt = new DataTransfer()
    dt.items.add(file)
    if (this.hasFileInputTarget) {
      this.fileInputTarget.files = dt.files
    }
    this._showFilePreview(file)
  }

  removeFile(event) {
    event.stopPropagation()
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ""
    }
    this.selections.hasFile = false
    if (this.hasFilePreviewTarget) {
      this.filePreviewTarget.style.display = "none"
    }
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.style.display = ""
    }
  }

  _showFilePreview(file) {
    this.selections.hasFile = true

    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
    }
    if (this.hasFileSizeTarget) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2)
      this.fileSizeTarget.textContent = `${sizeMB} Mo`
    }
    if (this.hasFilePreviewTarget) {
      this.filePreviewTarget.style.display = ""
    }
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.style.display = "none"
    }
  }

  // ──── Validation ────
  _validateStep(step) {
    switch (step) {
      case 1:
        if (!this.selections.category) {
          this._shake(
            this.element.querySelector('[data-card-group="category"]')
          )
          return false
        }
        return true
      case 2:
        if (!this.selections.hasFile) {
          this._shake(this.dropZoneTarget)
          return false
        }
        return true
      case 3: {
        const title = this.hasTitleInputTarget
          ? this.titleInputTarget.value.trim()
          : ""
        if (!title) {
          this._shake(
            this.titleInputTarget?.closest("div")
          )
          return false
        }
        return true
      }
      case 4:
        return true
      default:
        return true
    }
  }

  _shake(el) {
    if (!el) return
    el.classList.add("g4sp-wf-shake")
    setTimeout(() => el.classList.remove("g4sp-wf-shake"), 600)
  }

  // ──── Render ────
  _render() {
    const step = this.currentValue
    const pct = Math.round((step / this.totalValue) * 100)

    // Show/hide step panels
    this.stepTargets.forEach((panel) => {
      const s = parseInt(panel.dataset.step, 10)
      panel.style.display = s === step ? "" : "none"
    })

    // Update sidebar
    this.sidebarTargets.forEach((item) => {
      const s = parseInt(item.dataset.step, 10)
      item.classList.remove(
        "g4sp-wf-step--active",
        "g4sp-wf-step--done",
        "g4sp-wf-step--disabled"
      )
      if (s === step) {
        item.classList.add("g4sp-wf-step--active")
      } else if (s < step) {
        item.classList.add("g4sp-wf-step--done")
      } else {
        item.classList.add("g4sp-wf-step--disabled")
      }
    })

    // Progress bar
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = pct + "%"
    }
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent =
        `${step} / ${this.totalValue} — ${pct} %`
    }

    // Footer text
    if (this.hasFooterTarget) {
      const active = this.sidebarTargets.find(
        (i) => parseInt(i.dataset.step, 10) === step
      )
      if (active) {
        const title =
          active.querySelector("[data-step-title]")?.textContent || ""
        const sub =
          active.querySelector("[data-step-sub]")?.textContent || ""
        this.footerTarget.innerHTML =
          `<strong>${title}</strong> — ${sub}`
      }
    }

    // Prev/Next/Submit visibility
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.style.display = step > 1 ? "" : "none"
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.style.display =
        step < this.totalValue ? "" : "none"
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.style.display =
        step === this.totalValue ? "" : "none"
    }
  }
}
